import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRsvp } from '@/src/modules/rsvp';
import { Calendar, MapPin, Clock, Music, Heart, CheckCircle2, ChevronRight, Sparkles, Star } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { LightDiya } from '../../experiences/scene/interactive/LightDiya';
import { transliterateToMarathi } from '@/src/infrastructure/lib/transliterate';
import { extractCoupleNames, parseFlexibleDate, generateDynamicCeremonies, safeExtractCeremonies } from '@/src/infrastructure/lib/weddingDataHelper';
import './colors_and_type.css';
import './viewer.css';

const META: TemplateMetadata = {
  id: 'lagna-patrika-interactive',
  name: 'Lagna Patrika - Interactive',
  occasionType: 'WEDDING',
  description: 'An elegant, interactive Lagna Patrika featuring Marathi/English bilingual toggles, traditional mandap designs, and responsive layouts.',
  isPremium: true,
  tags: ['interactive', 'traditional', 'marathi', 'bilingual', 'animated'],
  colorScheme: { primary: '#BE123C', accent: '#D4AF37', bg: '#1A202C' },
};

// --- HINDU LUNAR CALENDAR (Saka Samvat + Tithi) ---
// Computes the approximate Tithi, Paksha, lunar month, and Saka year for a
// given Gregorian date. Uses mean lunar motion (± 1 Tithi accuracy, sufficient
// for invitation display). Reference new moon: Jan 6.10 2000 UT (JDE 2451550.09765).
function computeSakaTithi(date: Date): string {
  const y = date.getUTCFullYear();
  const mo = date.getUTCMonth() + 1; // 1-12
  const d = date.getUTCDate();

  // Saka Samvat year changes ~March 22
  const sakaYear = y - 78 - (mo < 3 || (mo === 3 && d < 22) ? 1 : 0);

  // Julian Day Number
  const a = Math.floor((14 - mo) / 12);
  const yy = y + 4800 - a;
  const mm = mo + 12 * a - 3;
  const jdn = d + Math.floor((153 * mm + 2) / 5) + 365 * yy
    + Math.floor(yy / 4) - Math.floor(yy / 100) + Math.floor(yy / 400) - 32045;

  // Mean synodic position (0..1 within current lunar cycle)
  const REF_JDN = 2451550.09765; // Jan 6.10 2000 UT new moon
  const SYNODIC = 29.530588853;
  const elapsed = jdn - REF_JDN;
  const pos = ((elapsed % SYNODIC) + SYNODIC) % SYNODIC; // days into cycle

  // Tithi (0-based index 0..29)
  const ti = Math.floor((pos / SYNODIC) * 30);
  const TITHI_NAMES = [
    'प्रतिपदा','द्वितीया','तृतीया','चतुर्थी','पंचमी',
    'षष्ठी','सप्तमी','अष्टमी','नवमी','दशमी',
    'एकादशी','द्वादशी','त्रयोदशी','चतुर्दशी','पौर्णिमा',
    'प्रतिपदा','द्वितीया','तृतीया','चतुर्थी','पंचमी',
    'षष्ठी','सप्तमी','अष्टमी','नवमी','दशमी',
    'एकादशी','द्वादशी','त्रयोदशी','चतुर्दशी','अमावस्या',
  ];
  const paksha = ti < 15 ? 'शुद्ध' : 'वद्य';
  const tithi  = TITHI_NAMES[ti];

  // Lunar month — named by the Gregorian month in which the new moon fell
  const nmDaysSinceEpoch = (jdn - pos) - 2440587.5;
  const nmMonth = new Date(nmDaysSinceEpoch * 86400000).getUTCMonth(); // 0=Jan
  const LUNAR_MONTHS = [
    'पौष','माघ','फाल्गुन','चैत्र','वैशाख','ज्येष्ठ',
    'आषाढ','श्रावण','भाद्रपद','आश्विन','कार्तिक','मार्गशीर्ष',
  ];
  const lunarMonth = LUNAR_MONTHS[nmMonth];

  // Marathi digits
  const toMr = (n: number) =>
    String(n).split('').map(c => '०१२३४५६७८९'[+c] ?? c).join('');

  return `शके ${toMr(sakaYear)} · ${lunarMonth} ${paksha} ${tithi}`;
}

// --- MULTI-LANGUAGE TRANSLATOR ---
const translateName = (name: string, targetLang: 'en' | 'mr'): string => {
  if (!name) return '';
  const lower = name.trim().toLowerCase();
  if (targetLang === 'mr') {
    if (lower === 'aarav' || lower === 'rohan') return 'रोहन';
    if (lower === 'priya' || lower === 'isha') return 'ईशा';
    if (lower === 'rahul sharma') return 'राहुल शर्मा';
    if (lower === 'priya mehta') return 'प्रिया मेहता';
    if (lower === 'your name' || lower === 'your event name') return 'तुमचे नाव';
  } else {
    if (lower === 'रोहन') return 'Rohan';
    if (lower === 'ईशा') return 'Isha';
    if (lower === 'राहुल शर्मा') return 'Rahul Sharma';
    if (lower === 'प्रिया मेहता') return 'Priya Mehta';
  }
  return name;
};

// --- SCROLL REVEAL COMPONENT ---
interface RevealProps {
  children: React.ReactNode;
  className?: string;
  as?: any;
  delay?: number;
}

const Reveal: React.FC<RevealProps> = ({ children, className = "", as: Tag = "div", delay = 0 }) => {
  const ref = useRef<HTMLElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setSeen(true);
      return;
    }
    el.classList.add("armed");
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        setSeen(true);
        cleanup();
      }
    };
    const check = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh * 0.9 && r.bottom > 0) finish();
    };
    const onScroll = () => check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Declare timeouts BEFORE calling check() so that cleanup() can safely
    // clearTimeout(t1/t2/safety) even if check() fires synchronously.
    const t1 = setTimeout(check, 120);
    const t2 = setTimeout(check, 500);
    const safety = setTimeout(finish, 2600);
    check();

    function cleanup() {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(safety);
    }
    return cleanup;
  }, []);

  const CustomTag = Tag as any;
  return (
    <CustomTag ref={ref} className={`reveal ${seen ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </CustomTag>
  );
};

// --- DOUBLE RULE CARD CORNERS ---
const Corners: React.FC<{ tone?: string }> = ({ tone = "" }) => {
  return (
    <React.Fragment>
      <img className={`cnr tl ${tone}`} src="/assets/motifs/corner.svg" alt="" />
      <img className={`cnr tr ${tone}`} src="/assets/motifs/corner.svg" alt="" />
      <img className={`cnr bl ${tone}`} src="/assets/motifs/corner.svg" alt="" />
      <img className={`cnr br ${tone}`} src="/assets/motifs/corner.svg" alt="" />
    </React.Fragment>
  );
};

// --- ORNAMENTAL DIVIDER ---
const Divider: React.FC<{ gold?: boolean; w?: number }> = ({ gold = true, w = 220 }) => {
  return (
    <img
      className="divider animate-pulse"
      style={{ width: w }}
      src={`/assets/motifs/divider${gold ? "-gold" : ""}.svg`}
      alt=""
    />
  );
};

// --- HERO SECTION ---
interface SectionProps {
  invite: any;
  invitationId?: string | null;
  isLive?: boolean;
}

const Hero: React.FC<SectionProps> = ({ invite }) => {
  const c = invite.couple;
  return (
    <section className="sec hero" data-screen-label="Hero">
      <img className="hero-toran" src="/assets/motifs/toran.svg" alt="" />
      <div className="hero-inner">
        <div className="hero-shloka ic-deva-acc">{invite.shloka}</div>
        <div className="hero-eyebrow">{invite.invocationEn}</div>
        <h1 className="hero-names">
          <span>{c.bride.en}</span>
          <span className="amp">&amp;</span>
          <span>{c.groom.en}</span>
        </h1>
        <div className="hero-deva">
          {c.bride.honorific} {c.bride.mr} <em>आणि</em> {c.groom.honorific} {c.groom.mr}
        </div>
        <Divider w={260} />
        <div className="hero-date">{invite.date.longEn}</div>
        {invite.date.tithi && <div className="hero-tithi">{invite.date.tithi}</div>}
      </div>
      <div className="scroll-cue" aria-hidden="true">
        <span>Scroll</span>
        <span className="scroll-dot" />
      </div>
    </section>
  );
};

// --- INVOCATION SECTION ---
const Invocation: React.FC<SectionProps> = ({ invite }) => {
  return (
    <section className="sec band-dark" data-screen-label="Invocation">
      <LightDiya primaryColor="#D4AF37" secondaryColor="#BE123C" backgroundColor="#1A202C">
        <Reveal className="invocation">
          <span className="om">ॐ</span>
          <pre className="shlok">{invite.blessing}</pre>
          <Divider w={180} />
          <p className="invocation-en">{invite.invocationEn}</p>
        </Reveal>
      </LightDiya>
    </section>
  );
};

// --- HOSTS SECTION ---
const Hosts: React.FC<SectionProps> = ({ invite }) => {
  return (
    <section className="sec" data-screen-label="Families">
      <Reveal className="eyebrow-c"><span className="ic-eyebrow">With the blessings of</span></Reveal>
      <div className="hosts">
        {invite.hosts.map((h: any, i: number) => (
          <Reveal key={i} className="host-slot fx-track" delay={i * 90}>
            <div className="host-card fx-focus">
              <Corners />
              <div className="host-role">{h.role}</div>
              <div className="host-mr">{h.mr}</div>
              <div className="host-en">{h.en}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

// --- CEREMONIES SECTION ---
const Ceremonies: React.FC<SectionProps> = ({ invite }) => {
  return (
    <section className="sec band-cream" data-screen-label="Ceremonies">
      <Reveal className="sec-head fx-track">
        <span className="ic-eyebrow">The celebrations</span>
        <h2 className="sec-title">Ceremonies & Timeline</h2>
        <Divider />
      </Reveal>
      <div className="timeline">
        <span className="timeline-spine" aria-hidden="true" />
        <span className="timeline-fill" aria-hidden="true" />
        {invite.ceremonies.map((cer: any, i: number) => (
          <Reveal key={cer.key} className={`tl-item fx-track ${i % 2 ? "right" : "left"}`} delay={i * 60}>
            <div className="tl-node" style={{ ['--c' as any]: `var(--${cer.tone}-500)` }} />
            <div className="tl-card fx-focus">
              <span className="tag" style={{ background: `var(--${cer.tone}-500)` }}>{cer.name}</span>
              <div className="tl-mr">{cer.mr}</div>
              <div className="tl-time">{cer.time}</div>
              <div className="tl-note">{cer.note}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

// --- VENUE SECTION ---
const Venue: React.FC<SectionProps> = ({ invite }) => {
  const v = invite.venue;
  return (
    <section className="sec" data-screen-label="Venue">
      <Reveal className="venue">
        <div className="venue-text">
          <span className="ic-eyebrow">Where</span>
          <h2 className="venue-name">{v.name}</h2>
          <div className="venue-mr">{v.nameMr}</div>
          <p className="venue-addr">{v.line}<br />{v.city}</p>
          <button
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.name + ' ' + v.line + ' ' + v.city)}`, '_blank', 'noopener,noreferrer')}
            className="btn btn-gold"
          >
            View directions
          </button>
        </div>
        <div className="venue-map" aria-hidden="true">
          <div className="map-grid" />
          <img className="map-mandala" src="/assets/motifs/mandala.svg" alt="" />
          <span className="map-pin"><img src="/assets/logo-mark.svg" alt="" /></span>
          <span className="map-hint">{v.mapHint}</span>
        </div>
      </Reveal>
    </section>
  );
};

// --- GALLERY SECTION ---
const Gallery: React.FC<SectionProps> = ({ invite }) => {
  return (
    <section className="sec band-cream" data-screen-label="Gallery">
      <Reveal className="sec-head fx-track">
        <span className="ic-eyebrow">Our story</span>
        <h2 className="sec-title">Moments together</h2>
        <Divider />
      </Reveal>
      <div className="gallery">
        {invite.gallery.map((g: any, i: number) => (
          <Reveal key={g.id} className={`gal-item fx-track ${i === 0 ? "wide" : ""}`} delay={i * 60}>
            <div className="gal-media">
              <img
                src={g.url}
                alt={g.caption}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <span className="gal-cap">{g.caption}</span>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

// --- RSVP SECTION ---
const RSVP: React.FC<SectionProps> = ({ invite, invitationId, isLive = false }) => {
  const [going, setGoing] = useState("yes");
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [guests, setGuests] = useState("2");
  const [note, setNote] = useState("");

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const sent = state === 'success';
  const submitting = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await submit({
      guestName: name.trim(),
      status: going === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: going === 'yes' ? (parseInt(guests, 10) || 1) : 1,
      message: note.trim() || undefined,
    });
  };

  return (
    <section className="sec band-dark" id="rsvp" data-screen-label="RSVP">
      <Reveal className="rsvp">
        <Corners tone="gold" />
        <span className="ic-eyebrow" style={{ color: "var(--marigold-400)" }}>Kindly respond</span>
        <h2 className="rsvp-title">Will you join us?</h2>
        {sent ? (
          <div className="rsvp-thanks" style={{ position: 'relative' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <p>{invite.closing.mr}</p>
            <p className="rsvp-thanks-en">{invite.closing.en}</p>
          </div>
        ) : !showForm ? (
          <div className="rsvp-gate" style={{ textAlign: 'center', padding: '20px 0' }}>
            {invite.rsvpLink ? (
              <a
                href={invite.rsvpLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-accent rsvp-btn"
              >
                RSVP Now
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-accent rsvp-btn"
                onClick={() => setShowForm(true)}
              >
                RSVP Now
              </button>
            )}
          </div>
        ) : (
          <form className="rsvp-form" onSubmit={handleSubmit}>
            <div className="seg">
              <button
                type="button"
                className={going === "yes" ? "on" : ""}
                onClick={() => setGoing("yes")}
              >
                Joyfully accepts
              </button>
              <button
                type="button"
                className={going === "no" ? "on" : ""}
                onClick={() => setGoing("no")}
              >
                Regretfully declines
              </button>
            </div>
            <label className="f">
              <span>Your name</span>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="focus:ring-2 focus:ring-amber-500"
              />
            </label>
            <div className="f-row">
              <label className="f">
                <span>Guests</span>
                <select
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="focus:ring-2 focus:ring-amber-500"
                >
                  <option value="1">1 guest</option>
                  <option value="2">2 guests</option>
                  <option value="3">3 guests</option>
                  <option value="4">4 guests</option>
                </select>
              </label>
            </div>
            <label className="f">
              <span>A note for the couple</span>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="शुभेच्छा…"
                className="focus:ring-2 focus:ring-amber-500"
              />
            </label>
            {state === 'error' && errorMessage && (
              <p style={{ color: '#ef4444', fontSize: 13, textAlign: 'center' }}>{errorMessage}</p>
            )}
            <button className="btn btn-accent rsvp-submit" type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send RSVP'}
            </button>
          </form>
        )}
      </Reveal>
    </section>
  );
};

// --- FOOTER SECTION ---
const Footer: React.FC<SectionProps> = ({ invite }) => {
  return (
    <footer className="sec foot">
      <Divider w={200} />
      <p className="foot-closing">{invite.closing.en}</p>
      {!invite.isPremium && (
        <>
          <div className="foot-brand">
            <img src="/assets/logo-mark.svg" alt="" />
            <span className="wm">Invite<span>Cloud</span></span>
          </div>
          <div className="foot-tag">{invite.brandTagline}</div>
        </>
      )}
    </footer>
  );
};

// --- PETALS CANVAS COMPONENT ---
interface PetalsProps {
  density?: number;
  active?: boolean;
}

const Petals: React.FC<PetalsProps> = ({ density = 1, active = true }) => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const COLORS = [
      ["#F7A93C", "#D97316"],
      ["#EF8C22", "#B85C12"],
      ["#FBC56B", "#EF8C22"],
      ["#E7A23C", "#C5392B"],
    ];

    const SPRITE = 40;
    const sprites = COLORS.map(([c1, c2]) => {
      const s = document.createElement("canvas");
      s.width = s.height = SPRITE;
      const sc = s.getContext("2d");
      if (!sc) return s;
      const cx = SPRITE / 2, r = SPRITE / 2 - 2;
      const g = sc.createRadialGradient(cx, cx, 1, cx, cx, r);
      g.addColorStop(0, c1);
      g.addColorStop(1, c2);
      sc.fillStyle = g;
      sc.beginPath();
      const lobes = 6;
      for (let i = 0; i <= lobes; i++) {
        const a = (i / lobes) * Math.PI * 2;
        const rr = r * (i % 2 === 0 ? 1 : 0.62);
        const x = cx + Math.cos(a) * rr, y = cx + Math.sin(a) * rr;
        i === 0 ? sc.moveTo(x, y) : sc.quadraticCurveTo(cx, cx, x, y);
      }
      sc.closePath();
      sc.fill();
      sc.fillStyle = c2;
      sc.globalAlpha = 0.65;
      sc.beginPath();
      sc.arc(cx, cx, r * 0.34, 0, Math.PI * 2);
      sc.fill();
      return s;
    });

    let w = 0, h = 0, dpr = 1, raf: number | null = null, running = false, t = 0;
    const N = Math.max(4, Math.round((reduce ? 6 : 16) * density));
    const petals: any[] = [];

    function resize() {
      if (!canvas || !ctx) return;
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function spawn(initial: boolean) {
      return {
        sp: (COLORS.length * Math.random()) | 0,
        x: Math.random() * w,
        y: initial ? Math.random() * h : -30 - Math.random() * 80,
        size: 10 + Math.random() * 16,
        v: 0.3 + Math.random() * 0.8,
        sway: 0.4 + Math.random() * 1.0,
        phase: Math.random() * Math.PI * 2,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.03,
        op: 0.55 + Math.random() * 0.4,
      };
    }

    function frame() {
      if (!running || !ctx) return;
      t += 1;
      ctx.clearRect(0, 0, w, h);
      for (const p of petals) {
        if (active) {
          p.y += p.v;
          p.x += Math.sin(t * 0.01 + p.phase) * p.sway * 0.4;
          p.rot += p.vr;
          if (p.y > h + 40) Object.assign(p, spawn(false));
        }
        ctx.save();
        ctx.globalAlpha = p.op;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.drawImage(sprites[p.sp], -p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      }
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (!running && !document.hidden) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    }

    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    resize();
    for (let i = 0; i < N; i++) petals.push(spawn(true));
    start();

    const onVis = () => (document.hidden ? stop() : start());
    let rt: any = null;
    const onResize = () => {
      clearTimeout(rt);
      rt = setTimeout(resize, 150);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("resize", onResize);

    return () => {
      stop();
      clearTimeout(rt);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("resize", onResize);
    };
  }, [density, active]);

  return <canvas ref={ref} className="petals-canvas" aria-hidden="true" />;
};

// --- SCROLL EFFECTS ---
interface ScrollFXProps {
  enabled?: boolean;
}

const ScrollFX: React.FC<ScrollFXProps> = ({ enabled = true }) => {
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const root = document.documentElement;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resetHero = () => {
      const hi = document.querySelector(".hero-inner") as HTMLElement;
      const ht = document.querySelector(".hero-toran") as HTMLElement;
      if (hi) {
        hi.style.transform = "";
        hi.style.opacity = "";
      }
      if (ht) {
        ht.style.transform = "translateX(-50%)";
      }
    };

    function reset() {
      document.querySelectorAll(".fx-track").forEach((el) => {
        (el as HTMLElement).style.setProperty("--focus", "1");
      });
      const tl = document.querySelector(".timeline") as HTMLElement;
      if (tl) tl.style.setProperty("--draw", "1");
      document.querySelectorAll(".tl-item").forEach((el) => {
        (el as HTMLElement).style.setProperty("--lit", "1");
      });
      root.style.setProperty("--read", "0");
      resetHero();
    }

    if (!enabled || reduce) {
      reset();
      return;
    }

    const clamp = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
    const smooth = (t: number) => t * t * (3 - 2 * t);
    let scheduled = false;

    function frame() {
      scheduled = false;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const center = vh / 2;
      const y = window.scrollY || window.pageYOffset || 0;

      document.querySelectorAll(".fx-track").forEach((el) => {
        const r = el.getBoundingClientRect();
        const c = r.top + r.height / 2;
        const f = clamp(1 - Math.abs(c - center) / (vh * 0.62));
        (el as HTMLElement).style.setProperty("--focus", smooth(f).toFixed(3));
      });

      const tl = document.querySelector(".timeline") as HTMLElement;
      if (tl) {
        const r = tl.getBoundingClientRect();
        const p = clamp((center - r.top) / (r.height || 1));
        tl.style.setProperty("--draw", p.toFixed(3));
        const fillY = r.top + (r.height || 0) * p;
        tl.querySelectorAll(".tl-item").forEach((item) => {
          const node = item.querySelector(".tl-node") as HTMLElement;
          const ny = node ? node.getBoundingClientRect().top : item.getBoundingClientRect().top;
          (item as HTMLElement).style.setProperty("--lit", fillY >= ny ? "1" : "0");
        });
      }

      const hi = document.querySelector(".hero-inner") as HTMLElement;
      if (hi) {
        const k = Math.min(1, y / vh);
        hi.style.transform = `translateY(${(y * 0.18).toFixed(1)}px) scale(${(1 - k * 0.06).toFixed(3)})`;
        hi.style.opacity = Math.max(0, 1 - k * 1.18).toFixed(3);
      }
      const ht = document.querySelector(".hero-toran") as HTMLElement;
      if (ht) ht.style.transform = `translateX(-50%) translateY(${(y * -0.14).toFixed(1)}px)`;

      const docH = document.documentElement.scrollHeight - vh;
      root.style.setProperty("--read", docH > 0 ? clamp(y / docH).toFixed(4) : "0");
    }

    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      rafRef.current = requestAnimationFrame(frame);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    frame();
    const t1 = setTimeout(frame, 220);
    const t2 = setTimeout(frame, 720);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [enabled]);

  return <div className="scroll-prog" aria-hidden="true" />;
};

// --- OPENING GATESmoment ---
interface OpeningGateProps {
  variant: string;
  invite: any;
  autoOpen?: boolean;
  onOpen: () => void;
}

const OpeningGate: React.FC<OpeningGateProps> = ({ variant, invite, autoOpen = true, onOpen }) => {
  const [phase, setPhase] = useState<"idle" | "opening" | "done">("idle");
  
  const open = useCallback(() => {
    setPhase((p) => {
      if (p !== "idle") return p;
      setTimeout(() => onOpen && onOpen(), 1500);
      setTimeout(() => setPhase("done"), 2100);
      return "opening";
    });
  }, [onOpen]);

  useEffect(() => {
    if (!autoOpen || phase !== "idle") return;
    let armed = false;
    const arm = setTimeout(() => {
      armed = true;
    }, 1000);
    const trigger = () => {
      if (armed) open();
    };
    const evs = ["wheel", "scroll", "touchmove", "touchstart", "pointerdown", "keydown"];
    evs.forEach((e) => window.addEventListener(e, trigger, { passive: true }));
    return () => {
      clearTimeout(arm);
      evs.forEach((e) => window.removeEventListener(e, trigger));
    };
  }, [autoOpen, phase, open]);

  if (phase === "done") return null;

  const c = invite.couple;
  const names = (
    <div className="gate-names">
      <span>{c.bride.en}</span>
      <em>&amp;</em>
      <span>{c.groom.en}</span>
    </div>
  );

  return (
    <div className={`gate gate--${variant} is-${phase}`} role="dialog" aria-label="Open the invitation">
      <div className="gate-bg" aria-hidden="true">
        <img className="gate-mandala" src="/assets/motifs/mandala-gold.svg" alt="" />
      </div>

      {variant === "gate" && (
        <React.Fragment>
          <img className="gate-toran" src="/assets/motifs/toran.svg" alt="" />
          <div className="doors">
            {["l", "r"].map((side) => (
              <button key={side} className={`door door--${side}`} onClick={open} aria-label="Open invitation">
                <span className="door-wood" aria-hidden="true" />
                <span className="door-border-molding" aria-hidden="true" />
                <div className="door-panels" aria-hidden="true">
                  <div className="door-panel dp-top">
                    <div className="dp-inner">
                      <img className="door-motif" src="/assets/motifs/mandala-gold.svg" alt="" />
                    </div>
                  </div>
                  <div className="door-panel dp-mid"><div className="dp-inner" /></div>
                  <div className="door-panel dp-bot"><div className="dp-inner" /></div>
                </div>
                <span className="door-studs-col" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map(i => <span key={i} className="stud" />)}
                </span>
                <span className="door-hinge dh--top" aria-hidden="true" />
                <span className="door-hinge dh--bot" aria-hidden="true" />
                <span className="door-knocker-wrap" aria-hidden="true">
                  <span className="door-knocker-plate" />
                  <span className="door-knocker-ring" />
                </span>
              </button>
            ))}
            <span className="door-seam" aria-hidden="true" />
          </div>
          <div className="door-frame-overlay" aria-hidden="true" />
          <div className="door-sill" aria-hidden="true" />
          <div className="gate-reveal">{names}</div>
        </React.Fragment>
      )}

      {variant === "envelope" && (
        <button className="env" onClick={open} aria-label="Open invitation">
          <div className="env-back">
            <div className="env-card">
              <div className="env-shloka">{invite.shloka}</div>
              {names}
              <img className="env-div" src="/assets/motifs/divider-gold.svg" alt="" />
              <div className="env-date">{invite.date.longEn}</div>
            </div>
          </div>
          <div className="env-body" />
          <div className="env-flap">
            <span className="seal"><img src="/assets/logo-mark.svg" alt="" /></span>
          </div>
        </button>
      )}

      {variant === "mandala" && (
        <button className="bloom" onClick={open} aria-label="Open invitation">
          <img className="bloom-ring bloom-ring--1" src="/assets/motifs/mandala-gold.svg" alt="" />
          <img className="bloom-ring bloom-ring--2" src="/assets/motifs/mandala-gold.svg" alt="" />
          <span className="bloom-core"><img src="/assets/logo-mark.svg" alt="" /></span>
          <div className="bloom-names">{names}</div>
        </button>
      )}

      {phase === "idle" && (
        <div className="gate-cue">
          <span className="gate-cue-mr">{variant === "mandala" ? "स्पर्श करा" : "उघडा"}</span>
          <span className="gate-cue-en">{autoOpen ? "Tap or scroll to open" : "Tap to open the invitation"}</span>
        </div>
      )}
    </div>
  );
};

// --- MAIN TEMPLATE EXPORT ---
export function LagnaPatrikaInteractive({ data }: { data: InvitationData }) {
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const customDataAny = (data.customData || {}) as any;
  const weddingData = customDataAny.wedding || {};
  const groomCustom = weddingData.groom || {};
  const brideCustom = weddingData.bride || {};

  const [openingStyle, setOpeningStyle] = useState<string>(
    weddingData.openingStyle || "gate"
  );
  const [opened, setOpened] = useState(false);
  const [gateKey, setGateKey] = useState(0);
  const mandalaRef = useRef<HTMLImageElement>(null);

  // Parse Invitation Data into local invitation model
  const invite = useMemo(() => {
    const parsedCouple = extractCoupleNames(data.eventName);
    const rawGroom = groomCustom.fullName || 
                     groomCustom.nickname || 
                     parsedCouple?.groom || 
                     "वर";
    const rawBride = brideCustom.fullName || 
                     brideCustom.nickname || 
                     parsedCouple?.bride || 
                     "वधू";

    const shloka = "॥ श्री गणेशाय नमः ॥";
    const blessing = data.customMessage || "वक्रतुंड महाकाय सूर्यकोटि समप्रभ ।\nनिर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा ॥";
    
    const hosts = weddingData.hosts && weddingData.hosts.length > 0 ? weddingData.hosts : [
      // Fallback: use standard data.hostName if customData.hosts unavailable
      ...(data.hostName ? [{
        role: 'Host',
        en: data.hostName,
        mr: transliterateToMarathi(data.hostName)
      }] : []),
      // Only include parents if actually configured in customData
      ...((brideCustom.parents || brideCustom.fatherName || brideCustom.parentsEn || brideCustom.parentsMr) ? [{
        role: "Bride's parents",
        en: brideCustom.parents || brideCustom.parentsEn || `Mr. & Mrs. ${brideCustom.fatherName || ''} & ${brideCustom.motherName || ''} ${brideCustom.family || ''}`.trim(),
        mr: brideCustom.parents || brideCustom.parentsMr || `श्री. व सौ. ${brideCustom.fatherName ? transliterateToMarathi(brideCustom.fatherName) : ''} व ${brideCustom.motherName ? transliterateToMarathi(brideCustom.motherName) : ''} ${brideCustom.family ? transliterateToMarathi(brideCustom.family) : ''}`.trim()
      }] : []),
      ...((groomCustom.parents || groomCustom.fatherName || groomCustom.parentsEn || groomCustom.parentsMr) ? [{
        role: "Groom's parents",
        en: groomCustom.parents || groomCustom.parentsEn || `Mr. & Mrs. ${groomCustom.fatherName || ''} & ${groomCustom.motherName || ''} ${groomCustom.family || ''}`.trim(),
        mr: groomCustom.parents || groomCustom.parentsMr || `श्री. व सौ. ${groomCustom.fatherName ? transliterateToMarathi(groomCustom.fatherName) : ''} व ${groomCustom.motherName ? transliterateToMarathi(groomCustom.motherName) : ''} ${groomCustom.family ? transliterateToMarathi(groomCustom.family) : ''}`.trim()
      }] : [])
    ];

    const eventDateObj = parseFlexibleDate(data.eventDate) || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const formattedEn = eventDateObj.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const formattedLongEn = eventDateObj.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(',', ' ·');
    
    const mrWeekdays: Record<string, string> = {
      'Sunday': 'रविवार', 'Monday': 'सोमवार', 'Tuesday': 'मंगळवार', 'Wednesday': 'बुधवार',
      'Thursday': 'गुरुवार', 'Friday': 'शुक्रवार', 'Saturday': 'शनिवार'
    };
    const enWeekday = eventDateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const mrWeekday = mrWeekdays[enWeekday] || 'शनिवार';
    
    const toMarathiDigits = (num: number | string) => {
      const digits: Record<string, string> = { '0':'०', '1':'१', '2':'२', '3':'३', '4':'४', '5':'५', '6':'६', '7':'७', '8':'८', '9':'९' };
      return String(num).split('').map(char => digits[char] || char).join('');
    };

    const date = {
      weekdayMr: mrWeekday,
      en: formattedEn,
      longEn: formattedLongEn,
      tithi: weddingData.date?.tithi || computeSakaTithi(eventDateObj),
      muhurt: weddingData.date?.muhurt || `शुभ मुहूर्त · सकाळी १०:४८`,
    };

    let venueName = data.eventVenue || "Saraswati Mangal Karyalay";
    let venueNameMr = weddingData.venue?.nameMr || "सरस्वती मंगल कार्यालय";
    let venueLine = weddingData.venue?.line || "Tilak Road, Sadashiv Peth";
    let venueCity = weddingData.venue?.city || "Pune, Maharashtra 411030";

    if (data.eventVenue && data.eventVenue.trim()) {
      const isDefaultVenue = 
        data.eventVenue.trim().toLowerCase() === "saraswati mangal karyalay" ||
        data.eventVenue.trim() === "सरस्वती मंगल कार्यालय";

      if (!isDefaultVenue) {
        const parts = data.eventVenue.includes('\n')
          ? data.eventVenue.split('\n').map(s => s.trim()).filter(Boolean)
          : data.eventVenue.split(',').map(s => s.trim()).filter(Boolean);

        if (parts.length >= 3) {
          venueName = parts[0];
          venueLine = parts.slice(1, -1).join(', ');
          venueCity = parts[parts.length - 1];
          venueNameMr = weddingData.venue?.nameMr || parts[0];
        } else if (parts.length === 2) {
          venueName = parts[0];
          venueLine = parts[1];
          venueCity = "";
          venueNameMr = weddingData.venue?.nameMr || parts[0];
        } else if (parts.length === 1) {
          venueName = parts[0];
          venueLine = "";
          venueCity = "";
          venueNameMr = weddingData.venue?.nameMr || parts[0];
        }
      }
    }

    const venue = {
      name: venueName,
      nameMr: venueNameMr,
      line: venueLine,
      city: venueCity,
      mapHint: weddingData.venue?.mapHint || "",
    };

    const ceremonies = safeExtractCeremonies(weddingData, eventDateObj, weddingData.date?.muhurtTime || "10:48 AM");

    const gallery = (data.mediaUrls && data.mediaUrls.length > 0) ? data.mediaUrls.map((url, idx) => ({
      id: `g${idx + 1}`,
      caption: weddingData.galleryCaps?.[idx] || `Moment ${idx + 1}`,
      url: url,
    })) : [
      { id: "g1", caption: "First trek together", url: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&w=1200" },
      { id: "g2", caption: "Diwali, 2024", url: "https://images.unsplash.com/photo-1542451313056-b7c8e626645f?auto=format&fit=crop&q=80&w=2000" },
      { id: "g3", caption: "The proposal", url: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=1200" },
      { id: "g4", caption: "Engagement", url: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&q=80&w=1200" },
    ];

    const closing = {
      mr: weddingData.closingMr || "आपल्या शुभ उपस्थितीने हा सोहळा अधिक संस्मरणीय होईल.",
      en: weddingData.closingEn || "Your presence and blessings will make our celebration complete.",
    };

    return {
      brandTagline: "Made with InviteCloud",
      shloka,
      blessing,
      invocationEn: weddingData.invocationEn || "With the blessings of Shri Ganesha and our elders, we joyfully invite you to the wedding of",
      couple: {
        bride: {
          en: brideCustom.nickname || rawBride,
          mr: brideCustom.nicknameMr || (rawBride ? transliterateToMarathi(rawBride) : ""),
          honorific: brideCustom.honorific || "कु.",
          family: brideCustom.family || "",
          familyMr: brideCustom.familyMr || (brideCustom.family ? transliterateToMarathi(brideCustom.family) : "")
        },
        groom: {
          en: groomCustom.nickname || rawGroom,
          mr: groomCustom.nicknameMr || (rawGroom ? transliterateToMarathi(rawGroom) : ""),
          honorific: groomCustom.honorific || "चि.",
          family: groomCustom.family || "",
          familyMr: groomCustom.familyMr || (groomCustom.family ? transliterateToMarathi(groomCustom.family) : "")
        },
        monogram: `${(brideCustom.nickname || rawBride)[0] || 'I'}&${(groomCustom.nickname || rawGroom)[0] || 'R'}`,
      },
      hosts,
      date,
      venue,
      ceremonies,
      gallery,
      closing,
    };
  }, [data, groomCustom, brideCustom, weddingData]);

  // reseal + replay the opening
  const reseal = useCallback(() => {
    setOpened(false);
    setGateKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const chooseVariant = useCallback((v: string) => {
    setOpeningStyle(v);
    reseal();
  }, [reseal]);

  // background mandala parallax
  useEffect(() => {
    let raf: number | null = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const y = window.scrollY || 0;
        if (mandalaRef.current) {
          mandalaRef.current.style.transform = `translate(-50%, ${-50 + y * 0.04}%) rotate(${y * 0.02}deg)`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("locked", !opened);
    return () => {
      document.body.classList.remove("locked");
    };
  }, [opened]);

  return (
    <div className={`viewer ${opened ? "is-open" : "is-sealed"}`}>
      <div className="bg-layer" aria-hidden="true">
        <img ref={mandalaRef} className="bg-mandala" src="/assets/motifs/mandala.svg" alt="" />
      </div>

      <Petals active={true} density={opened ? 1 : 0.5} />
      <ScrollFX enabled={true} />

      <main className="doc">
        <Hero invite={invite} />
        <Invocation invite={invite} />
        <Hosts invite={invite} />
        <Ceremonies invite={invite} />
        <Venue invite={invite} />
        <Gallery invite={invite} />
        <RSVP invite={invite} invitationId={invitationId} isLive={isLive} />
        <Footer invite={invite} />
      </main>

      {opened && (
        <a
          className="rsvp-bar"
          href="#rsvp"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          <span className="rsvp-bar-mr">सस्नेह निमंत्रण</span>
          <span className="rsvp-bar-cta">RSVP →</span>
        </a>
      )}

      <OpeningGate
        key={gateKey}
        variant="gate"
        invite={invite}
        autoOpen={true}
        onOpen={() => setOpened(true)}
      />
    </div>
  );
}

registry.register({ metadata: META, component: LagnaPatrikaInteractive });
